import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import { useId } from 'react-id-generator';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext,
  FieldError
} from 'react-hook-form';

import { cn } from '@epoch-ui/lib/utils';
import { Label } from '@epoch-ui/components/Form/label';
import { ErrorMessage } from '@epoch-ui/components/ErrorMessage';

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: ControllerProps<TFieldValues, TName>
): React.JSX.Element => {
  const value = React.useMemo(() => ({ name: props.name }), [props.name]);

  return (
    <FormFieldContext.Provider value={value}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

interface FormFieldState {
  id: string;
  name: string;
  formItemId: string;
  formDescriptionId: string;
  formMessageId: string;
  invalid: boolean;
  isDirty: boolean;
  isTouched: boolean;
  error?: FieldError;
}

const useFormField = (): FormFieldState => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  if (!fieldContext) {
    throw new Error('useFormField must be used within a <FormField>');
  }

  if (!itemContext) {
    throw new Error('useFormField must be used within a <FormItem>');
  }

  const { name } = fieldContext;

  const fieldState = getFieldState(name, formState);

  const { id } = itemContext;

  return {
    id,
    name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue | null>(null);

const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const [id] = useId();
    const value = React.useMemo(() => ({ id }), [id]);

    return (
      <FormItemContext.Provider value={value}>
        <div ref={ref} className={cn('', className)} {...props} />
      </FormItemContext.Provider>
    );
  }
);
FormItem.displayName = 'FormItem';

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { formItemId } = useFormField();

  return <Label ref={ref} className={cn('tw-m-0', className)} htmlFor={formItemId} {...props} />;
});
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ className, ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
      className={cn('tw-mt-2', className)}
    />
  );
});
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn('tw-m-0 tw-text-xs tw-leading-4 tw-text-muted-foreground', className)}
      {...props}
    />
  );
});
FormDescription.displayName = 'FormDescription';

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();

  let body = children;

  if (error) {
    body = Array.isArray(error) ? error[0].message : String(error.message);
  }

  if (!body) {
    return null;
  }

  return (
    <ErrorMessage ref={ref} id={formMessageId} {...props}>
      {body}
    </ErrorMessage>
  );
});
FormMessage.displayName = 'FormMessage';

interface CompositeFormMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {
  priority?: string[];
}

/**
 * A component to render error messages for complex and nested form fields that consists of multiple sub-inputs.
 *
 * @param priority - The priority of sub-inputs to display the error message for.
 * It's helpful when the form field has multiple sub-inputs and you want to display the error message for a specific sub-input.
 */
const CompositeFormMessage = React.forwardRef<HTMLParagraphElement, CompositeFormMessageProps>(
  ({ priority = [], children, ...props }, ref) => {
    const { error, formMessageId } = useFormField();

    const errorMessage = React.useMemo(() => {
      if (!error) {
        return null;
      }

      // Priority 1: Root level error message
      if (error.message) {
        return error.message;
      }

      // Priority 2: Sub-field errors in specified order or object key order
      if (typeof error === 'object' && error !== null) {
        const subFieldKeys = priority.length > 0 ? priority : Object.keys(error);

        const subErrorMessage = subFieldKeys
          .map((key) => (error as any)[key] as FieldError | undefined)
          .find((subError) => subError?.message)?.message;

        if (subErrorMessage) {
          return subErrorMessage;
        }
      }

      return null;
    }, [error, priority]);

    const body = children || errorMessage;

    if (!body) {
      return null;
    }

    return (
      <ErrorMessage ref={ref} id={formMessageId} {...props}>
        {body}
      </ErrorMessage>
    );
  }
);
CompositeFormMessage.displayName = 'CompositeFormMessage';

const FormFieldSet = React.forwardRef<
  HTMLFieldSetElement,
  React.HTMLAttributes<HTMLFieldSetElement>
>(({ className, ...props }, ref) => {
  return (
    <fieldset ref={ref} className={cn('tw-flex tw-flex-col tw-gap-6', className)} {...props} />
  );
});
FormFieldSet.displayName = 'FormFieldSet';

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  CompositeFormMessage,
  FormField,
  FormFieldSet
};
